import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AppSettings } from '../config/app-settings';
import { firstValueFrom } from 'rxjs';

interface WidgetJwtPayload {
  user_jwt: string;
  expires_at: string;
  app_id: string;
  ttl_seconds: number;
}

interface WidgetJwtResponse {
  success: boolean;
  data?: WidgetJwtPayload;
}

interface ArdiaWidgetConfig {
  appId: string;
  userJwt: string;
  app_id?: string;
  user_jwt?: string;
  apiBase?: string;
  position?: string;
  locale?: string;
  debug?: boolean;
  onTokenExpired?: () => Promise<string>;
}

interface ArdiaWidgetApi {
  init: (config?: ArdiaWidgetConfig) => unknown;
  destroy?: () => void;
  open?: () => void;
  close?: () => void;
}

declare global {
  interface Window {
    ARDIA_WIDGET?: ArdiaWidgetConfig;
    ArdiaWidget?: ArdiaWidgetApi;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ArdiaWidgetService {
  private static readonly BUNDLE_SCRIPT_ID = 'ardia-widget-bundle';
  private static readonly REFRESH_SAFETY_WINDOW_SECONDS = 30;
  private static readonly MOBILE_MEDIA_QUERY = '(max-width: 640px)';

  private scriptLoadPromise: Promise<void> | null = null;
  private mountPromise: Promise<void> | null = null;
  private mounted = false;
  private cachedJwtPayload: WidgetJwtPayload | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private mountedPosition = '';
  private mobileMediaQueryList: MediaQueryList | null = null;
  private readonly handleMobileViewportChange = () => {
    if (!this.mounted) {
      return;
    }

    const nextPosition = this.resolveWidgetPosition();
    if (nextPosition === this.mountedPosition) {
      return;
    }

    void this.mount(true).catch((error: unknown) => {
      console.error('No se pudo reajustar el widget de Ardia:', error);
    });
  };

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  async mount(forceReinitialize: boolean = false): Promise<void> {
    if (!this.isEnabled()) {
      return;
    }

    if (!this.hasAuthToken()) {
      return;
    }

    if (this.mountPromise) {
      return this.mountPromise;
    }

    if (this.mounted && !forceReinitialize) {
      return;
    }

    this.mountPromise = this.mountInternal(forceReinitialize).finally(() => {
      this.mountPromise = null;
    });

    return this.mountPromise;
  }

  unmount(): void {
    this.clearRefreshTimer();
    this.detachMobileViewportListener();

    try {
      window.ArdiaWidget?.destroy?.();
    } catch {
      // No bloqueamos logout por errores del widget.
    }

    this.mounted = false;
    this.mountedPosition = '';
    this.cachedJwtPayload = null;
    window.ARDIA_WIDGET = undefined;
  }

  private async mountInternal(forceReinitialize: boolean): Promise<void> {
    const payload = await this.fetchWidgetJwt(true);
    const config = this.buildConfig(payload);
    window.ARDIA_WIDGET = config;

    if (window.ArdiaWidget?.init) {
      await this.callWidgetInit(config);
      this.mounted = true;
      this.mountedPosition = config.position || '';
      this.attachMobileViewportListener();
      this.logDebug('initialized using existing runtime');
      return;
    }

    await this.ensureBundleLoaded();

    if (!window.ArdiaWidget?.init && !forceReinitialize) {
      this.mounted = true;
      this.mountedPosition = config.position || '';
      this.attachMobileViewportListener();
      this.logDebug('initialized by bundle auto-init');
      return;
    }

    if (window.ArdiaWidget?.init) {
      await this.callWidgetInit(config);
    }

    this.mounted = true;
    this.mountedPosition = config.position || '';
    this.attachMobileViewportListener();
    this.logDebug('initialized after bundle load');
  }

  private buildConfig(payload: WidgetJwtPayload): ArdiaWidgetConfig {
    const configuredLocale = String(environment.ardiaWidgetLocale || '').trim();
    const configuredApiBase = String(environment.ardiaWidgetApiBase || '').trim();

    const config: ArdiaWidgetConfig = {
      appId: payload.app_id,
      userJwt: payload.user_jwt,
      app_id: payload.app_id,
      user_jwt: payload.user_jwt,
      position: this.resolveWidgetPosition(),
      debug: !!environment.ardiaWidgetDebug,
      onTokenExpired: () => this.handleTokenExpired(),
    };

    if (configuredLocale) {
      config.locale = configuredLocale;
    }

    if (configuredApiBase) {
      config.apiBase = configuredApiBase;
    }

    return config;
  }

  private resolveWidgetPosition(): string {
    const defaultPosition = String(environment.ardiaWidgetPosition || '').trim();
    const mobilePosition = String(environment.ardiaWidgetMobilePosition || '').trim();

    if (mobilePosition && this.isMobileViewport()) {
      return mobilePosition;
    }

    return defaultPosition;
  }

  private isMobileViewport(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(ArdiaWidgetService.MOBILE_MEDIA_QUERY).matches;
  }

  private attachMobileViewportListener(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    if (this.mobileMediaQueryList) {
      return;
    }

    this.mobileMediaQueryList = window.matchMedia(
      ArdiaWidgetService.MOBILE_MEDIA_QUERY,
    );
    this.mobileMediaQueryList.addEventListener(
      'change',
      this.handleMobileViewportChange,
    );
  }

  private detachMobileViewportListener(): void {
    if (!this.mobileMediaQueryList) {
      return;
    }

    this.mobileMediaQueryList.removeEventListener(
      'change',
      this.handleMobileViewportChange,
    );
    this.mobileMediaQueryList = null;
  }

  private async ensureBundleLoaded(): Promise<void> {
    if (window.ArdiaWidget?.init) {
      return;
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    const bundleUrl = String(environment.ardiaWidgetBundleUrl || '').trim();
    if (!bundleUrl) {
      throw new Error('ardiaWidgetBundleUrl no está configurado');
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const existing = this.document.getElementById(
        ArdiaWidgetService.BUNDLE_SCRIPT_ID,
      ) as HTMLScriptElement | null;

      if (existing) {
        if (existing.dataset['loaded'] === 'true') {
          resolve();
          return;
        }

        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener(
          'error',
          () => reject(new Error(`No se pudo cargar ${bundleUrl}`)),
          { once: true },
        );
        return;
      }

      const script = this.document.createElement('script');
      script.id = ArdiaWidgetService.BUNDLE_SCRIPT_ID;
      script.src = bundleUrl;
      script.async = true;
      script.addEventListener(
        'load',
        () => {
          script.dataset['loaded'] = 'true';
          resolve();
        },
        { once: true },
      );
      script.addEventListener(
        'error',
        () => {
          reject(new Error(`No se pudo cargar ${bundleUrl}`));
        },
        { once: true },
      );
      const head = this.document.head;
      if (!head) {
        reject(new Error('No se encontró document.head para cargar el widget'));
        return;
      }

      head.appendChild(script);
    }).finally(() => {
      this.scriptLoadPromise = null;
    });

    return this.scriptLoadPromise;
  }

  private async callWidgetInit(config: ArdiaWidgetConfig): Promise<void> {
    const maybePromise = window.ArdiaWidget?.init(config);
    if (this.isPromiseLike(maybePromise)) {
      await maybePromise;
    }
  }

  private async handleTokenExpired(): Promise<string> {
    const payload = await this.fetchWidgetJwt(true);
    window.ARDIA_WIDGET = this.buildConfig(payload);
    this.logDebug('token refreshed via onTokenExpired');
    return payload.user_jwt;
  }

  private async fetchWidgetJwt(forceRefresh: boolean): Promise<WidgetJwtPayload> {
    const now = Date.now();
    if (!forceRefresh && this.cachedJwtPayload) {
      const expiryTimestamp = Date.parse(this.cachedJwtPayload.expires_at);
      if (
        Number.isFinite(expiryTimestamp) &&
        expiryTimestamp - now >
          ArdiaWidgetService.REFRESH_SAFETY_WINDOW_SECONDS * 1000
      ) {
        return this.cachedJwtPayload;
      }
    }

    let params = new HttpParams();
    const configuredAppId = String(environment.ardiaWidgetAppId || '').trim();
    if (configuredAppId) {
      params = params.set('app_id', configuredAppId);
    }

    const response = await firstValueFrom(
      this.http.get<WidgetJwtResponse>(`${AppSettings.API_URI}/me/widget-jwt`, {
        params,
      }),
    );

    if (!response?.success || !response.data?.user_jwt || !response.data.app_id) {
      throw new Error('Respuesta inválida al solicitar /me/widget-jwt');
    }

    this.cachedJwtPayload = response.data;
    this.scheduleRefresh(response.data);
    return response.data;
  }

  private scheduleRefresh(payload: WidgetJwtPayload): void {
    this.clearRefreshTimer();

    const expiresAtMs = Date.parse(payload.expires_at);
    const ttlMs = Math.max(0, Number(payload.ttl_seconds) || 0) * 1000;
    const fallbackExpiryMs = Date.now() + ttlMs;
    const targetExpiryMs = Number.isFinite(expiresAtMs) ? expiresAtMs : fallbackExpiryMs;
    const refreshAtMs =
      targetExpiryMs - ArdiaWidgetService.REFRESH_SAFETY_WINDOW_SECONDS * 1000;
    const delayMs = Math.max(10_000, refreshAtMs - Date.now());

    this.refreshTimer = setTimeout(() => {
      void this.fetchWidgetJwt(true).catch((error: unknown) => {
        this.logDebug('refresh timer failed', error);
      });
    }, delayMs);
  }

  private clearRefreshTimer(): void {
    if (!this.refreshTimer) {
      return;
    }

    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  private isEnabled(): boolean {
    return !!environment.ardiaWidgetEnabled;
  }

  private hasAuthToken(): boolean {
    return !!(
      localStorage.getItem('jwt_token') ?? sessionStorage.getItem('jwt_token')
    );
  }

  private logDebug(message: string, extra?: unknown): void {
    if (!environment.ardiaWidgetDebug) {
      return;
    }

    if (extra === undefined) {
      console.info(`[ArdiaWidget] ${message}`);
      return;
    }

    console.info(`[ArdiaWidget] ${message}`, extra);
  }

  private isPromiseLike(value: unknown): value is Promise<unknown> {
    return (
      typeof value === 'object' &&
      value !== null &&
      'then' in value &&
      typeof (value as { then?: unknown }).then === 'function'
    );
  }
}
